import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { regularSubjectList } from '@shared/utils/utils';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Classroom, Grade, Teacher } from '../../../shared/model/classroom.models';
import { Subject } from '../../../shared/model/course.models';
import { AuthService } from '../../../shared/service/auth.service';
import { ClassroomService } from '../../../shared/service/classroom.service';
import { CourseService } from '../../../shared/service/course.service';
import { SharedService } from '../../../shared/service/shared.service';
import { back } from '@common';

@Component({
  selector: 'fl-edit-teacher-info',
  templateUrl: './edit-teacher-info.component.html',
  styleUrls: ['./edit-teacher-info.component.scss'],
  standalone: false,
})
export class EditTeacherInfoComponent implements OnInit {
  teacher: Teacher;
  form: UntypedFormGroup;
  subjectList: Array<Subject>;
  // 所有学科列表
  totalSubjectList: Subject[];

  gradeList: Array<Grade> = [];
  classType: number;
  classroomList: Array<Classroom>;
  addClassId: number;
  manageClassroomList: Classroom[];

  userId: number;
  siteId: number;

  private route: ActivatedRoute = inject(ActivatedRoute);
  private courseSrv: CourseService = inject(CourseService);
  private authSrv: AuthService = inject(AuthService);
  private classSrv: ClassroomService = inject(ClassroomService);
  public sharedSrv: SharedService = inject(SharedService);
  private msgSrv: NzMessageService = inject(NzMessageService);
  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  ngOnInit() {
    this.userId = parseInt(this.route.snapshot.paramMap.get('id'), 10);
    this.siteId = this.route.snapshot.queryParams['siteId'];
    this.form = this.fb.group({
      subject: [null, [Validators.required]],
      userGrade: [null],
      manageGrade: [null, Validators.required],
      teachClassroom: [null],
      questionAssociateNode: [false],
      isActive: [true],
      siteManager: [false],
      gradeDirector: [false],
      subjectDirector: [false],
      classroomDirector: [false],
      manageClassroom: [null, [Validators.required]],
      // 下列数据提交时不要带上
      gradeList: [null],
      classType: [0],
      addClassId: [null],
    });

    // OnPush：以下 then 续体均非模板事件，异步赋值后需主动标记
    this.courseSrv.getSubjectList().then(result => {
      this.totalSubjectList = regularSubjectList(result);
      this.cdr.markForCheck();
    });

    this.classSrv.getGradeList({ siteId: this.siteId }).then(result => {
      this.gradeList = result;
      this.cdr.markForCheck();
    });

    this.classSrv.classSearch({ siteId: this.siteId ?? this.sharedSrv.user.site_id }).then(r => {
      this.manageClassroomList = r;
      this.cdr.markForCheck();
    });

    this.form.get('gradeList').valueChanges.subscribe(async grade => {
      this.form.get('addClassId').setValue(null);
      this.classroomList = [];
      if (grade === null) {
        return;
      }
      this.classroomList = await this.classSrv.classSearch({
        gradeId: grade,
      });
      this.cdr.markForCheck();
    });

    this.form.get('userGrade').valueChanges.subscribe(grade => {
      this.subjectList = this.totalSubjectList.filter(item => item.grade === grade);
    });

    this.form.get('classroomDirector').valueChanges.subscribe(value => {
      if (value) {
        const control = new UntypedFormControl(null, Validators.required);
        this.form.addControl('manageClassroom', control);
      } else {
        this.form.removeControl('manageClassroom');
      }
    });

    this.form.get('classType').valueChanges.subscribe(() => this.form.get('addClassId').setValue(null));

    this.authSrv.getTeacherInfo(this.userId).then((r: Teacher) => {
      this.teacher = r;

      this.form.patchValue({
        userGrade: Number(r.userGrade),
        subject: r.subject,
        siteManager: r.siteManager,
        gradeDirector: r.gradeDirector,
        subjectDirector: r.subjectDirector,
        classroomDirector: r.classroomDirector,
        isActive: r.isActive,
        questionAssociateNode: r.questionAssociateNode,
        ...(r.classroomDirector && { manageClassroom: r.manageClassroom.map(i => i.id) }),
        teachClassroom: r.teachClassroom,
        manageGrade: r.manageGrade.map(i => i.id),
      });

      this.form.get('gradeDirector').valueChanges.subscribe(() => this.switchManageGrade());

      this.form.get('subjectDirector').valueChanges.subscribe(() => this.switchManageGrade());
      this.cdr.markForCheck();
    });
  }

  /**
   * 判断是否显示管理年级
   */
  switchManageGrade(): void {
    const gradeDirector = this.form.get('gradeDirector').value;
    const subjectDirector = this.form.get('subjectDirector').value;
    if (gradeDirector || subjectDirector) {
      if (this.form.get('manageGrade')) return;
      const classroomsList = this.form.get('teachClassroom').value;
      const gradeList = classroomsList.map(i => {
        const { gradeId } = this.manageClassroomList.filter(c => c.id === i.id)[0];
        return gradeId;
      });
      const control = new UntypedFormControl(null, Validators.required);
      if (gradeList.length > 0) {
        control.setValue([...new Set(gradeList)]);
      }
      this.form.addControl('manageGrade', control);
    } else {
      this.form.removeControl('manageGrade');
    }
  }

  /**
   * 添加任教班级
   * @param classId 班级ID
   */
  addTeachClassroom(classId: number): void {
    const list = this.form.get('teachClassroom').value;
    if (list && list.some(i => i.id === classId)) {
      this.msgSrv.warning('当前班级已存在!');
      return;
    }
    const { id, text, type } = this.manageClassroomList.filter(i => i.id === classId)[0];
    list.push({ id, type, name: text });
    this.form.get('teachClassroom').setValue(list);
  }

  /**
   * 删除指定任教班级
   * @param classId 班级ID
   */
  deleteTeachClass(classId: number): void {
    let list = this.form.get('teachClassroom').value;
    list = list.filter(i => i.id !== classId);
    this.form.get('teachClassroom').setValue(list);
  }

  submit(): void {
    for (const i in this.form.controls) {
      this.form.controls[i].markAsDirty();
      this.form.controls[i].updateValueAndValidity();
    }
    if (this.form.valid) {
      this.msgSrv.info('正在保存.');
      const form = this.form.value;
      form.teachClassroom = form.teachClassroom.map(i => i.id);
      delete form.gradeList;
      delete form.classType;
      delete form.addClassId;
      this.authSrv.updateTeacherInfo(this.teacher.id, form).then(res => {
        this.msgSrv.success('保存成功.');
        back();
      });
    }
  }

  protected readonly back = back;
}
